import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type {
  LocalizedText,
  QuestionOption,
} from "../../api/CertRoadmap/types";
import BilingualText from "./BilingualText";
import type { RoadmapLanguage, StepKey } from "./types";

type RoadmapQuestionnaireProps = {
  stepIndex: number;
  totalSteps: number;
  currentStep: StepKey;
  currentLabel?: LocalizedText | string;
  currentOptions: QuestionOption[];
  selectedIds: string[];
  multiSelect: boolean;
  requiredSelectionCount?: number;
  isSubmitting: boolean;
  canContinue: boolean;
  language: RoadmapLanguage;
  onSelect: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
};

const RoadmapQuestionnaire = ({
  stepIndex,
  totalSteps,
  currentStep,
  currentLabel,
  currentOptions,
  selectedIds,
  multiSelect,
  requiredSelectionCount,
  isSubmitting,
  canContinue,
  language,
  onSelect,
  onBack,
  onNext,
}: RoadmapQuestionnaireProps) => {
  const isLastStep = stepIndex >= totalSteps - 1;
  const text =
    language === "ar"
      ? {
          matcher: "مطابقة الشهادات",
          title: "لنحدد شهادتك المهنية القادمة.",
          optional: "اختياري — تخطَّ هذا السؤال إذا لم تُكمل أي شهادة بعد.",
          trackHint:
            "يمكنك اختيار مسار واحد، أو دمج مسار القيادة مع مسار آخر.",
          specializationHint: (selected: number, required: number) =>
            `اختر تخصصًا واحدًا لكل مسار مهني (${selected} من ${required}).`,
          back: "السابق",
          generating: "جارٍ إنشاء الخارطة...",
          generate: "أنشئ خارطتي المهنية",
          next: "السؤال التالي",
        }
      : {
          matcher: "Certification matcher",
          title: "Let's map your next credential.",
          optional: "Optional — skip if you haven't completed any yet.",
          trackHint:
            "Choose one track, or combine Leadership with one other track.",
          specializationHint: (selected: number, required: number) =>
            `Choose exactly one specialization for each selected career track (${selected} of ${required}).`,
          back: "Back",
          generating: "Generating...",
          generate: "Generate my roadmap",
          next: "Next question",
        };

  return (
    <>
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-secondary">
            {text.matcher}
          </p>
          <h2 className="mt-2 text-2xl font-bold text-primary sm:text-3xl">
            {text.title}
          </h2>
        </div>
        <span className="shrink-0 text-sm font-bold text-lightGray">
          {stepIndex + 1} <span className="font-normal">/ {totalSteps}</span>
        </span>
      </div>

      <div className="mb-9 h-2 overflow-hidden rounded-full bg-[#edf0ee]">
        <motion.div
          className="h-full rounded-full bg-secondary"
          animate={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
        />
      </div>

      <div>
        <p className="mb-6 text-xl font-bold leading-snug text-primary sm:text-2xl">
          <BilingualText value={currentLabel} language={language} />
        </p>
        {currentStep === "career_track" && (
          <p className="-mt-3 mb-6 text-sm leading-6 text-lightGray">
            {text.trackHint}
          </p>
        )}
        {currentStep === "specialization" && requiredSelectionCount && (
          <p className="-mt-3 mb-6 text-sm leading-6 text-lightGray">
            {text.specializationHint(
              selectedIds.length,
              requiredSelectionCount
            )}
          </p>
        )}
        <div
          className={`grid gap-3 ${
            currentOptions.length > 6
              ? "sm:grid-cols-2"
              : "sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {currentOptions.map((option) => {
            const selected = selectedIds.includes(option.id);

            return (
              <button
                key={option.id}
                type="button"
                role={multiSelect ? "checkbox" : "radio"}
                aria-checked={selected}
                onClick={() => onSelect(option.id)}
                className={`group flex min-h-[88px] items-center justify-between gap-3 rounded-2xl border-2 p-4 text-start text-sm font-bold transition-all ${
                  selected
                    ? "border-secondary bg-secondary/10 text-primary"
                    : "border-[#e8edeb] text-textPrimary hover:-translate-y-0.5 hover:border-secondary/50"
                }`}
              >
                <BilingualText value={option.label} language={language} />
                <span
                  className={`flex size-7 shrink-0 items-center justify-center border ${
                    multiSelect ? "rounded-md" : "rounded-full"
                  } ${
                    selected
                      ? "border-secondary bg-secondary text-white"
                      : "border-[#d4dbd7] text-transparent"
                  }`}
                >
                  <Check size={15} />
                </span>
              </button>
            );
          })}
        </div>

        {currentStep === "completed_credentials" && (
          <p className="mt-4 text-sm text-lightGray">
            {text.optional}
          </p>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button
          disabled={stepIndex === 0 || isSubmitting}
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-bold text-lightGray transition hover:text-primary disabled:opacity-30"
        >
          {language === "ar" ? (
            <ChevronRight size={18} />
          ) : (
            <ChevronLeft size={18} />
          )}
          {text.back}
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue || isSubmitting}
          className="flex items-center gap-2 rounded-full bg-secondary px-6 py-3 text-sm font-bold text-white shadow-lg shadow-secondary/20 transition hover:-translate-y-0.5 hover:bg-[#df4315] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting
            ? text.generating
            : isLastStep
              ? text.generate
              : text.next}
          {language === "ar" ? (
            <ArrowLeft size={17} />
          ) : (
            <ArrowRight size={17} />
          )}
        </button>
      </div>
    </>
  );
};

export default RoadmapQuestionnaire;
